import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
interface Star {
  id: number;
  x: number;
  y: number;
}
interface Connection {
  from: number;
  to: number;
}
interface OrionConstellationProps {
  starCount?: number;
  color?: string;
  className?: string;
  onComplete?: () => void;
}
const OrionConstellation: React.FC<OrionConstellationProps> = ({
  starCount = 14,
  color = 'currentColor',
  className = '',
  onComplete
}) => {
  const [stars, setStars] = useState<Star[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [animationComplete, setAnimationComplete] = useState(false);
  useEffect(() => {
    // Generate star positions
    const generatedStars: Star[] = [];
    for (let i = 0; i < starCount; i++) {
      generatedStars.push({
        id: i,
        x: Math.random() * 80 + 10,
        y: Math.random() * 60 + 10
      });
    }
    setStars(generatedStars);
    // Generate connections (create a constellation pattern)
    const generatedConnections: Connection[] = [];
    for (let i = 0; i < generatedStars.length - 1; i++) {
      // Connect to next star
      if (Math.random() > 0.3) {
        generatedConnections.push({
          from: i,
          to: i + 1
        });
      }
      // Occasionally connect to a random star
      if (Math.random() > 0.7 && i < generatedStars.length - 2) {
        const randomTarget = Math.floor(Math.random() * (generatedStars.length - i - 2)) + i + 2;
        generatedConnections.push({
          from: i,
          to: randomTarget
        });
      }
    }
    setConnections(generatedConnections);
    // Trigger completion after animation
    const timer = setTimeout(() => {
      setAnimationComplete(true);
      onComplete?.();
    }, starCount * 100 + connections.length * 100 + 500);
    return () => clearTimeout(timer);
  }, [starCount, onComplete]);
  return <div className={`relative w-full h-96 ${className}`} data-id="element-140">
      <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" data-id="element-141">
        {/* Connections */}
        {connections.map((conn, index) => {
        const fromStar = stars[conn.from];
        const toStar = stars[conn.to];
        if (!fromStar || !toStar) return null;
        return <motion.line key={`conn-${index}`} x1={fromStar.x} y1={fromStar.y} x2={toStar.x} y2={toStar.y} stroke={color} strokeWidth="0.1" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 0.4
        }} transition={{
          duration: 0.8,
          delay: starCount * 0.1 + index * 0.1,
          ease: 'easeInOut'
        }} data-id="element-142" />;
      })}
        {/* Stars */}
        {stars.map((star, index) => <motion.circle key={`star-${star.id}`} cx={star.x} cy={star.y} r="0.5" fill={color} initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: index * 0.1,
        ease: 'easeOut'
      }} data-id="element-143">
            <animate attributeName="opacity" values="1;0.5;1" dur="3s" repeatCount="indefinite" begin={`${index * 0.3}s`} data-id="element-144" />
          </motion.circle>)}
      </svg>
      {/* Title overlay */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: animationComplete ? 1 : 0,
      y: animationComplete ? 0 : 20
    }} transition={{
      duration: 1,
      delay: 0.5
    }} className="absolute inset-0 flex flex-col items-center justify-center text-center px-4" data-id="element-145">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-146">
          ORION by Grupo Reflex
        </h2>
        <p className="text-xl text-purple-300 italic max-w-2xl" data-id="element-147">
          "Donde las ideas se alinean para crear historias que brillan."
        </p>
      </motion.div>
    </div>;
};
export default OrionConstellation;